import DataTable, { Column } from "../../components/DataTable";
import Tag from "../../components/Tags";
import { trash } from "../../asset";
import { useNavigate } from "react-router-dom";
import { formatDateTime } from "../../util/functions/UniversalFunctions";
import { Assignment } from "../../types/assignmentType";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import assignmentApis from "../../api/assignmentApis";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useSelector } from "react-redux";
import { setFilter } from "../../features/assignment/assignmentSlice";

interface Props {
  list: Assignment[];
  loading: boolean;
  refreshList: () => void;
}

export default function AssignmentList({ list, loading, refreshList }: Props) {
  const navigate = useNavigate();
  const { confirm, modal } = useConfirmToast();
  const dispatch = useDispatch<AppDispatch>();
  const filters = useSelector((state: RootState) => state.assignment.filters);

  const deleteAssignment = async (assignmentId: number) => {
    const result = await confirm(
      "Are you sure you want to delete this Assignment?",
    );
    if (!result) return;

    try {
      await assignmentApis.deleteAssignment(assignmentId.toString());
      if (refreshList) refreshList();
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const handleViewAssignment = (item: Assignment) => {
    navigate(`/exams-and-assignments/view-assignment/${item.assignmentId}`);
  };

  const handleViewSubmission = (item: Assignment) => {
    navigate(`/exams-and-assignments/view-submission/${item.assignmentId}`);
  };

  const columns: Column<Assignment>[] = [
    { label: "SL NO.", accessor: "assignmentId" },
    {
      label: "Assignment Title",
      accessor: "assignmentTitle",
      searchable: true,
      render: (item) => (
        <div className="text-neutral-400 text-sm font-bold">
          {item.assignmentTitle || "---"}
        </div>
      ),
    },
    {
      label: "For Class",
      accessor: "forClass",
      render: (item) => (
        <div className="flex flex-wrap gap-1 max-w-44">
          {item.classes?.length ? (
            item.classes.map((cls, index) => (
              <Tag
                key={cls.classId ?? index}
                text={cls.className}
                color={index % 2 === 0 ? "primary" : "success"}
              />
            ))
          ) : (
            <Tag text="---" />
          )}
        </div>
      ),
    },
    {
      label: "Created On",
      accessor: "assignmentDate",
      searchable: true,
      render: (item) => (
        <div className="max-w-24">{formatDateTime(item.assignmentDate)}</div>
      ),
    },
    {
      label: "Due Date",
      accessor: "assignmentDueDate",
      searchable: true,
      render: (item) => (
        <div className="max-w-24">{formatDateTime(item.assignmentDueDate)}</div>
      ),
    },
    // {
    //   label: "Created By",
    //   accessor: "createdBy",
    //   render: (item) => (
    //     <p className="max-w-44 line-clamp-2">{item.createdBy || "---"}</p>
    //   ),
    // },
    {
      label: "View",
      accessor: "result",
      render: (item) => (
        <div className="flex flex-col gap-3">
          <Tag
            color="success"
            text="View Assignment"
            onClick={() => handleViewAssignment(item)}
          />
          <Tag
            color="primary"
            text="View Submission"
            onClick={() => handleViewSubmission(item)}
          />
        </div>
      ),
    },
    {
      label: "Action",
      accessor: "action",
      render: (item) => (
        <button onClick={() => deleteAssignment(item.assignmentId)}>
          <img src={trash} alt="delete" className="w-[18px] h-[18px]" />
        </button>
      ),
    },
  ];

  return (
    <div className="grid gap-7 bg-white px-6 py-5 rounded-lg">
      <DataTable
        columns={columns}
        data={list}
        isLoading={loading}
        searchFilters={filters}
        onSearchChange={(accessor, value) => {
          if (accessor.includes("Date") && value) {
            dispatch(
              setFilter({
                key: accessor,
                value: new Date(value).toISOString(),
              }),
            );
          } else {
            dispatch(setFilter({ key: accessor, value }));
          }
        }}
      />

      {modal}
    </div>
  );
}
